import {
  type Cart as CartData,
  CartType,
} from "@cappelendamm/e-commerce-services";
import { isDigitalServiceItem, isFreightItem } from "@/cart/selectors";

export const VIPPS_EXPRESS_INELIGIBLE_REASON = {
  CART_TYPE: "CART_TYPE",
  EMPTY_CART: "EMPTY_CART",
  FREE_ORDER: "FREE_ORDER",
  CURRENCY: "CURRENCY",
  DIGITAL_SERVICE: "DIGITAL_SERVICE",
  LONG_HORIZON_PREORDER: "LONG_HORIZON_PREORDER",
} as const;

export type VippsExpressIneligibleReason =
  (typeof VIPPS_EXPRESS_INELIGIBLE_REASON)[keyof typeof VIPPS_EXPRESS_INELIGIBLE_REASON];

export type VippsExpressEligibility =
  | { eligible: true }
  | { eligible: false; reason: VippsExpressIneligibleReason };

/**
 * Whether a cart can be paid with Vipps Hurtigkasse. Mirrors the rules of the
 * normal checkout: only ordinary shop carts, nothing that must be invoiced
 * (long-horizon preorders), and no digital services, which need an
 * organization or Feide user.
 *
 * Who may use it (signed-out visitors only) is decided by the caller, since
 * the session is not part of the cart.
 */
export const getVippsExpressEligibility = (
  cart: CartData | null,
  { longHorizonPreorderSkus }: { longHorizonPreorderSkus: Set<string> },
): VippsExpressEligibility => {
  if (!cart || cart.cartType !== CartType.SHOP) {
    return {
      eligible: false,
      reason: VIPPS_EXPRESS_INELIGIBLE_REASON.CART_TYPE,
    };
  }

  const products = cart.items.filter((item) => !isFreightItem(item));
  if (products.length === 0) {
    return {
      eligible: false,
      reason: VIPPS_EXPRESS_INELIGIBLE_REASON.EMPTY_CART,
    };
  }
  if (cart.total.gross <= 0) {
    return {
      eligible: false,
      reason: VIPPS_EXPRESS_INELIGIBLE_REASON.FREE_ORDER,
    };
  }
  if (cart.total.currency !== "NOK") {
    return {
      eligible: false,
      reason: VIPPS_EXPRESS_INELIGIBLE_REASON.CURRENCY,
    };
  }
  if (products.some(isDigitalServiceItem)) {
    return {
      eligible: false,
      reason: VIPPS_EXPRESS_INELIGIBLE_REASON.DIGITAL_SERVICE,
    };
  }
  if (products.some((item) => longHorizonPreorderSkus.has(item.variant.sku))) {
    return {
      eligible: false,
      reason: VIPPS_EXPRESS_INELIGIBLE_REASON.LONG_HORIZON_PREORDER,
    };
  }

  return { eligible: true };
};
